import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import type { ReactNode } from 'react'
import { Link as RouterLink, Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle'
import { instrumentQuickCreatePath } from '@/features/instruments/lib'
import {
  LuBrain,
  LuChevronRight,
  LuClock,
  LuFolderClosed,
  LuInfo,
  LuListChecks,
  LuPencil,
  LuUsers,
  LuZap,
} from '@/shared/icons/lucide'
import { useSessionStore } from '@/stores/useSessionStore'

/** Lovable primary blue `#2563EB` and brand violet. */
const LOVABLE_PRIMARY = '#2563EB'
const LOVABLE_VIOLET = '#7C3AED'
const NAVY = '#0f172a'
const MUTED = '#64748b'
const CARD_BORDER = '#e2e8f0'

type Bullet = { icon: ReactNode; title: string; body: string }

type OptionCardProps = Readonly<{
  tone: 'primary' | 'violet'
  title: string
  description: string
  bullets: Bullet[]
  cta: string
  footer: string
  onClick: () => void
  testId: string
}>

function OptionCard({
  tone,
  title,
  description,
  bullets,
  cta,
  footer,
  onClick,
  testId,
}: OptionCardProps) {
  const isPrimary = tone === 'primary'
  const accent = isPrimary ? LOVABLE_PRIMARY : LOVABLE_VIOLET
  const accentSoft = isPrimary ? 'rgba(37, 99, 235, 0.1)' : 'rgba(124, 58, 237, 0.1)'
  const footerBg = isPrimary ? 'rgba(37, 99, 235, 0.06)' : 'rgba(124, 58, 237, 0.06)'

  return (
    <Box
      data-testid={testId}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: '#fff',
        border: `1px solid ${CARD_BORDER}`,
        borderRadius: '16px',
        p: { xs: 2.5, sm: 3, lg: 3.5 },
        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          borderColor: `${accent}4D`,
          boxShadow: '0 12px 24px rgba(15, 23, 42, 0.1)',
        },
      }}
    >
      <Box
        sx={{
          mx: 'auto',
          width: { xs: 56, sm: 64 },
          height: { xs: 56, sm: 64 },
          borderRadius: '16px',
          bgcolor: accentSoft,
          color: accent,
          display: 'grid',
          placeItems: 'center',
          flexShrink: 0,
        }}
      >
        {isPrimary ? <LuFolderClosed size={26} aria-hidden /> : <LuZap size={26} aria-hidden />}
      </Box>

      <Typography
        component="h2"
        sx={{
          mt: 2,
          textAlign: 'center',
          fontWeight: 700,
          fontSize: { xs: 18, sm: 20, lg: 24 },
          letterSpacing: '-0.02em',
          color: NAVY,
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          mt: 1,
          textAlign: 'center',
          fontSize: 14,
          lineHeight: 1.6,
          color: MUTED,
        }}
      >
        {description}
      </Typography>

      <Stack
        component="ul"
        spacing={2}
        sx={{
          mt: 2.5,
          flex: 1,
          borderTop: `1px solid ${CARD_BORDER}`,
          pt: 2.5,
          m: 0,
          pl: 0,
          listStyle: 'none',
        }}
      >
        {bullets.map((b) => (
          <Box
            key={b.title}
            component="li"
            sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '999px',
                bgcolor: accentSoft,
                color: accent,
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
              }}
            >
              {b.icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: NAVY }}>
                {b.title}
              </Typography>
              <Typography sx={{ fontSize: 14, color: MUTED }}>{b.body}</Typography>
            </Box>
          </Box>
        ))}
      </Stack>

      <Button
        type="button"
        onClick={onClick}
        data-testid={`${testId}-cta`}
        endIcon={<LuChevronRight size={18} aria-hidden />}
        sx={{
          mt: 5,
          width: '100%',
          justifyContent: 'space-between',
          bgcolor: accent,
          color: '#fff',
          textTransform: 'none',
          fontWeight: 700,
          fontSize: { xs: 14, sm: 16 },
          borderRadius: '12px',
          px: 2.5,
          py: 1.75,
          '&:hover': { bgcolor: accent, opacity: 0.92 },
        }}
      >
        {cta}
      </Button>

      <Box
        sx={{
          mt: 1.5,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          borderRadius: '12px',
          p: 1.75,
          bgcolor: footerBg,
          color: MUTED,
          fontSize: 14,
        }}
      >
        <Box component="span" sx={{ mt: 0.25, flexShrink: 0, display: 'flex' }}>
          <LuInfo size={16} aria-hidden />
        </Box>
        {footer}
      </Box>
    </Box>
  )
}

export function GetStartedPage() {
  useDocumentTitle('Build New Research')
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const companyId = searchParams.get('companyId')?.trim() || null
  const role = useSessionStore((s) => s.user?.role)

  const campaignSetupPath = companyId
    ? `/campaigns/new/setup?companyId=${encodeURIComponent(companyId)}`
    : '/campaigns/new/setup'

  // Story actors are Admin / Editor only — Viewers stay read-only on Research.
  if (role === 'VIEWER') {
    return <Navigate to="/unauthorized" replace />
  }

  return (
    <Box
      data-testid="get-started-page"
      sx={{ mx: 'auto', width: '100%', maxWidth: 1200 }}
    >
      <Button
        component={RouterLink}
        to="/"
        startIcon={<ArrowBackIcon />}
        data-testid="get-started-back-dashboard"
        sx={{
          mb: 2,
          textTransform: 'none',
          color: MUTED,
          fontWeight: 600,
          px: 0,
          '&:hover': { bgcolor: 'transparent', color: NAVY },
        }}
      >
        Back to Dashboard
      </Button>

      <Typography
        component="h1"
        sx={{
          fontWeight: 700,
          fontSize: { xs: 20, sm: 24, lg: 30 },
          letterSpacing: '-0.02em',
          color: NAVY,
        }}
      >
        How would you like to get started?
      </Typography>
      <Typography
        sx={{
          mt: 0.75,
          fontSize: { xs: 14, sm: 16 },
          color: MUTED,
        }}
      >
        Choose the best way to build your survey or instrument
      </Typography>

      <Box
        sx={{
          mt: 2.5,
          display: 'grid',
          gap: { xs: 2.5, lg: 3 },
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
          alignItems: 'stretch',
        }}
      >
        <OptionCard
          tone="primary"
          testId="get-started-campaign"
          title="1. Create New Campaign"
          description="Create a campaign with clear objectives. Our AI will help break down your goals into key themes (Lines of Inquiry) and organize your research structure."
          bullets={[
            {
              icon: <LuBrain size={16} aria-hidden />,
              title: 'AI helps breakdown campaign objectives',
              body: 'Into actionable themes and Lines of Inquiry',
            },
            {
              icon: <LuListChecks size={16} aria-hidden />,
              title: 'Structured research setup',
              body: 'Organize your study with Lines of Inquiry',
            },
            {
              icon: <LuUsers size={16} aria-hidden />,
              title: 'Add team members',
              body: 'Collaborate with your team on the campaign',
            },
          ]}
          cta="Create New Campaign"
          footer="Recommended for complex research with multiple themes and long-term collaboration"
          onClick={() => navigate(campaignSetupPath)}
        />

        <OptionCard
          tone="violet"
          testId="get-started-quick"
          title="2. Build Quick Instrument"
          description="Bypass the campaign setup and start building your instrument right away. Perfect for quick surveys or single-focus research."
          bullets={[
            {
              icon: <LuZap size={16} aria-hidden />,
              title: 'Quick and direct',
              body: 'Jump straight into the question builder',
            },
            {
              icon: <LuClock size={16} aria-hidden />,
              title: 'Save time',
              body: 'No need to create a full campaign structure',
            },
            {
              icon: <LuPencil size={16} aria-hidden />,
              title: 'Flexible & focused',
              body: 'Ideal for short surveys or specific questions',
            },
          ]}
          cta="Build Quick Instrument"
          footer="Great for quick surveys, ad-hoc research, or testing specific hypotheses"
          onClick={() => navigate(instrumentQuickCreatePath())}
        />
      </Box>
    </Box>
  )
}
